import axios from "axios";

// Audio goes to Blob Storage in blocks, each retried on its own, and is
// then committed with one block list. It used to go up in a single PUT of
// up to 400 MB, which one dropped connection lost entirely.
// https://learn.microsoft.com/rest/api/storageservices/put-block
export const BLOCK_SIZE = 8 * 1024 * 1024;
export const ATTEMPTS = 4;
export const RETRY_DELAYS_MS = [2000, 5000, 15000];

// Six characters naming the file, from its name, size and modification
// time (FNV-1a), so that a resumed upload never takes the blocks another
// file left on the same blob for its own and splices the two together.
export const fileTag = (file) => {
  const text = `${file.name}|${file.size}|${file.lastModified}`;
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  }
  return (hash >>> 0).toString(36).padStart(6, "0").slice(-6);
};

// Azure requires every block id of a blob to have the same length, the
// blocks an earlier upload left on it included: 12 characters before
// encoding, as the "block-000000" ids sent before had.
export const blockId = (tag, index) =>
  btoa(`${tag}${String(index).padStart(6, "0")}`);

const withQuery = (url, query) =>
  `${url}${url.includes("?") ? "&" : "?"}${query}`;

// No response (a dropped connection), timeouts, throttling and server
// errors can succeed on another try; a refused SAS cannot.
const isTransient = (error) => {
  const status = error?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetries = async (send) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await send();
    } catch (error) {
      if (attempt >= ATTEMPTS || !isTransient(error)) throw error;
      await sleep(RETRY_DELAYS_MS[attempt - 1]);
    }
  }
};

// The blocks an interrupted upload left on the blob, which Azure keeps
// uncommitted for a week: block id -> size. Empty when there is no blob yet
// or no answer, so that everything is sent.
// https://learn.microsoft.com/rest/api/storageservices/get-block-list
export const uncommittedBlocks = async (sasUrl) => {
  try {
    const { data } = await axios.get(
      withQuery(sasUrl, "comp=blocklist&blocklisttype=uncommitted"),
      { responseType: "text" }
    );
    const xml = new DOMParser().parseFromString(data, "application/xml");
    return new Map(
      Array.from(xml.getElementsByTagName("Block")).map((block) => [
        block.getElementsByTagName("Name")[0]?.textContent,
        Number(block.getElementsByTagName("Size")[0]?.textContent),
      ])
    );
  } catch (error) {
    return new Map();
  }
};

// Uploads `file` to the blob behind `sasUrl`, which needs read and write
// permission. `onProgress(sentBytes, totalBytes)` follows the upload. With
// `resume`, blocks an interrupted upload of this same file already stored
// are not sent again.
export const uploadInBlocks = async (
  sasUrl,
  file,
  { blockSize = BLOCK_SIZE, onProgress = () => {}, resume = false } = {}
) => {
  const stored = resume ? await uncommittedBlocks(sasUrl) : new Map();
  const tag = fileTag(file);
  const ids = [];
  let sent = 0;
  for (let start = 0; start < file.size; start += blockSize) {
    const chunk = file.slice(start, Math.min(start + blockSize, file.size));
    const id = blockId(tag, ids.length);
    const sentBefore = sent;
    if (stored.get(id) !== chunk.size) {
      await withRetries(() =>
        axios.put(
          withQuery(sasUrl, `comp=block&blockid=${encodeURIComponent(id)}`),
          chunk,
          {
            headers: { "Content-Type": "application/octet-stream" },
            onUploadProgress: (e) =>
              onProgress(sentBefore + Math.min(e.loaded, chunk.size), file.size),
          }
        )
      );
    }
    sent += chunk.size;
    ids.push(id);
    onProgress(sent, file.size);
  }

  const blockList = ids.map((id) => `<Latest>${id}</Latest>`).join("");
  await withRetries(() =>
    axios.put(
      withQuery(sasUrl, "comp=blocklist"),
      `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockList}</BlockList>`,
      // Only headers the single PUT this replaced already sent: the
      // storage account's CORS rules may list the allowed ones, and the
      // browser would then refuse the commit of every upload. Nothing
      // reads the original audio's content type.
      { headers: { "Content-Type": "application/xml" } }
    )
  );
};
